import { FormattedMessage } from "react-intl";
import { Navigate, useNavigate } from "react-router-dom";

import { Button } from "@mui/material";

import Logo from "assets/logo.svg";

import { Token } from "../../api/types/token";
import { LangBtn } from "../../components/LangBtn";
import { Routes } from "../../consts/routes";
import { useAuth } from "../../hooks/useAuth";

import { useLogin } from "./api/useLogin";
import { useTooltips } from "./api/useTooltips";
import { LoginForm, LoginFormShape } from "./components/LoginForm";

export function LoginView() {
  const { setAuthData, isAuthenticated, user, clearAuthData } = useAuth();
  const navigate = useNavigate();

  const handleSuccess = (data: unknown) => {
    setAuthData(data as Token);
    navigate(`/${Routes.Dashboard}`);
  };

  const { login, isError, isPending } = useLogin({ onSuccess: handleSuccess });

  // Check if user is authenticated AND has a valid role before redirecting
  const hasValidRole = user && user.Role;
  const shouldRedirect = false; // isAuthenticated && hasValidRole;

  // Check if user is authenticated but lacks proper role (authorization issue)
  const hasAuthorizationIssue = isAuthenticated && !hasValidRole;

  // Only fetch tooltips when user is not authenticated to prevent API calls during redirect loops
  const { tooltips } = useTooltips(!shouldRedirect);

  const description = (tooltips?.Tooltips ?? [])[0]?.Description;

  function handleLogin(data: LoginFormShape) {
    login(data);
  }

  function handleLogout() {
    clearAuthData();
  }

  return !shouldRedirect ? (
    <div className="xs:grid-cols-1 lg:grid grid-cols-2 max-h-full h-screen">
      <div className="max-h-full h-screen pt-16 sm:pt-16 lg:pt-16 xl:pt-16 2xl:pt-32 flex flex-col items-center overflow-auto">
        <img
          src={Logo}
          alt="Logo"
          className="w-[240px] mb-10 sm:mb-10 lg:mb-10 xl:mb-10 2xl:mb-20"
        />
        <h1 className="font-poppins text-5xl font-bold mb-5">
          <FormattedMessage
            id="LOGIN.TITLE"
            defaultMessage="Hello!"
            description="Welcome message on login page"
          />
        </h1>

        {description?.length && !hasAuthorizationIssue && (
          <p
            className="w-3/4 mx-auto text-center mb-8"
            dangerouslySetInnerHTML={{ __html: description ?? "" }}
          />
        )}

        {hasAuthorizationIssue ? (
          <>
            <p className="text-base mb-4 text-red-600 text-center max-w-md">
              <FormattedMessage
                id="LOGIN.AUTHORIZATION_ERROR"
                defaultMessage="Your account does not have the required permissions to access this application."
                description="Error message for users without proper role"
              />
            </p>
            <p className="text-sm mb-8 text-gray-600 text-center max-w-md">
              <FormattedMessage
                id="LOGIN.CONTACT_ADMIN"
                defaultMessage="Please contact your administrator to request access."
                description="Help text for authorization error"
              />
            </p>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout}
              size="large">
              <FormattedMessage
                id="LOGIN.LOGOUT"
                defaultMessage="Log Out"
                description="Logout button text"
              />
            </Button>
          </>
        ) : (
          <>
            <p className="text-base mb-8">
              <FormattedMessage
                id="LOGIN.SUBTITLE"
                defaultMessage="Please <b>log in</b> to the expert application"
                description="Login page text under hello text"
                values={{
                  b: chunks => <b>{chunks}</b>,
                }}
              />
            </p>
            <LoginForm
              onSubmit={handleLogin}
              isError={isError}
              isPending={isPending}
            />
          </>
        )}
        <div className="flex-1 flex flex-col justify-end w-full">
          <div className="flex flex-row w-full justify-between items-center px-8 py-4">
            <div className="w-[200px] hidden">
              <LangBtn />
            </div>
            <span className="block text-gray-500">
              <FormattedMessage
                id="SIDEBAR.COPYRIGHT"
                defaultMessage="Copyright © FarmTree® B.V."
              />
            </span>
          </div>
        </div>
      </div>
      <div
        className="hidden lg:block lg:max-h-screen lg:overflow-hidden bg-cover"
        style={{
          backgroundImage: `url(${require("assets/river.jpeg")})`,
        }}></div>
    </div>
  ) : (
    <Navigate to="/dashboard" />
  );
}
